import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import {
  resolveUserMessage,
  isDisplayableMessage,
} from "../utils/errorMessages";

/**
 * Resolves user-facing copy for an API failure, for the flows that opt out of
 * the global error modal and render their own inline message.
 *
 * Returns a function taking the error and the flow's own wording. Safe server
 * text wins, then anything the shared resolver knows is more specific than
 * "try again" (an expired session, a server fault), then the flow wording.
 * Never returns the developer string carried on `error.message`.
 */
export function useApiErrorMessage() {
  const { t } = useTranslation();

  return useCallback(
    (error, flowCopy) => {
      const fallbackCopy =
        flowCopy ||
        t("errorModal.generic", "Something went wrong. Please try again.");
      if (!error) return fallbackCopy;

      // A bare string is untrusted: it can be a third-party SDK message or a
      // developer string. It only ships if it clears the same leak filter.
      if (typeof error === "string") {
        return isDisplayableMessage(error) ? error : fallbackCopy;
      }

      const { key, fallback, serverText } = resolveUserMessage(error);
      if (serverText) return serverText;

      // A rejection with no status did not come from parseApiError, so the
      // resolver has nothing better than the flow's own wording to offer.
      if (!error.status) return fallbackCopy;

      return key === "errorModal.generic" ? fallbackCopy : t(key, fallback);
    },
    [t]
  );
}
